import React, { createContext, useContext } from 'react';
import CreateScheduleModal from '../components/modals/CreateScheduleModal';
import { useCRUDModal } from './shared/useCRUDModal';
import { CRUDModalOpenOptions } from './shared/types';
import { API_BASE_URL } from '../config/api';  // ✅ Industry Standard: Environment-aware URLs

/**
 * Schedule entity type
 */
export interface Schedule {
  id: string;
  name: string;
  description?: string;
  tag: string;
  executionMode: 'parallel' | 'sequential';
  browser: string;
  cronSchedule: string;
  timezone: string;
  enabled: boolean;
  isRecurring: boolean;
  executionOrder: string[] | null;
  webhookId?: string | null;  // Optional specific webhook for notifications
  disableNotifications?: boolean;  // Skip all webhook notifications
  lastRunAt?: string;
  lastRunStatus?: string;
  lastRunId?: string;
  nextRunAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Options for opening the schedule modal
 * Extends CRUDModalOpenOptions for consistency across the app
 */
export interface ScheduleOptions extends CRUDModalOpenOptions {
  // No additional fields needed - recordId, onSuccess, onCancel inherited
}

/**
 * Schedule context type definition
 * Provides global access to scheduling functionality
 * 
 * Industry Standard: Simplified API using shared CRUD hook
 */
interface ScheduleContextType {
  openScheduleModal: (options?: ScheduleOptions) => void;
}

// Create context with undefined default (will throw if used outside provider)
const ScheduleContext = createContext<ScheduleContextType | undefined>(undefined);

/**
 * Global Schedule Provider
 * 
 * Manages a single modal instance for scheduling across the entire application.
 * 
 * Industry Standard Pattern: Context + Hook (like Material-UI, Chakra UI, React Router)
 * - Single source of truth for modal state
 * - No prop drilling
 * - Type-safe with TypeScript
 * - Accessible from any component via useSchedule() hook
 * 
 * @example
 * // In App.tsx
 * <ScheduleProvider>
 *   <AppContent />
 * </ScheduleProvider>
 * 
 * // In any component
 * const { openScheduleModal } = useSchedule();
 * 
 * // Simple usage
 * openScheduleModal();
 * 
 * // Advanced usage with pre-filled data
 * openScheduleModal({
 *   tag: '@smoke',
 *   name: 'Daily Smoke Tests',
 *   executionMode: 'sequential',
 *   onSuccess: () => logger.info('Schedule created!')
 * });
 */
export const ScheduleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ✅ INDUSTRY STANDARD: Use shared CRUD modal hook
  // Handles all fetch, loading, error logic automatically
  const modal = useCRUDModal<Schedule>({
    entityName: 'schedule',
    fetchEndpoint: `${API_BASE_URL}/api/schedules`
  });

  const contextValue: ScheduleContextType = {
    openScheduleModal: modal.open
  };

  return (
    <ScheduleContext.Provider value={contextValue}>
      {children}
      
      {/* Global Modal Instance - Rendered once at app root */}
      {/* ✅ NEW: Modal receives fetched schedule data and explicit mode */}
      <CreateScheduleModal
        isOpen={modal.isOpen}
        mode={modal.mode}
        schedule={modal.record}
        isLoading={modal.isLoading}
        error={modal.error}
        onClose={modal.close}
        onSaved={modal.handleSuccess}
      />
    </ScheduleContext.Provider>
  );
};

/**
 * Custom hook to access schedule context
 * 
 * Industry Standard: Similar to useToast(), useNavigate(), useSnackbar()
 * Simplified API using shared CRUD hook
 * 
 * @throws {Error} If used outside ScheduleProvider
 * 
 * @example
 * const { openScheduleModal } = useSchedule();
 * 
 * // Create mode (empty form)
 * openScheduleModal();
 * 
 * // Create with callbacks
 * openScheduleModal({
 *   onSuccess: () => fetchSchedules(),
 *   onCancel: () => logger.info('Cancelled')
 * });
 * 
 * // Edit mode (fetches schedule automatically)
 * openScheduleModal({
 *   recordId: 'schedule-123',
 *   onSuccess: () => fetchSchedules()
 * });
 */
export const useSchedule = (): ScheduleContextType => {
  const context = useContext(ScheduleContext);
  
  if (!context) {
    throw new Error(
      'useSchedule must be used within a ScheduleProvider. ' +
      'Wrap your app with <ScheduleProvider> in App.tsx'
    );
  }
  
  return context;
};

// Export context for advanced use cases (testing, debugging)
export { ScheduleContext };

